import {
  eventTarget,
  getEnabledElement,
  Types,
  utilities,
} from '@cornerstonejs/core';
import { annotation } from '@cornerstonejs/tools';
import { Internal } from '@gleamer/types';
import { CommandsManager, ExtensionManager, ServicesManager } from '@ohif/core';
import { ActionCreators } from 'redux-undo';
import { BUTTON_IDS } from './components/organisms/toolbar/constants';
import { OBSERVATION_EVENTS } from './contexts/ObservationContext/ObservationContext';
import { OVERLAY_EVENTS } from './contexts/OverlayStateContext/OverlayStateContext';
import { mipProtocolModule } from './hanging-protocols/mip';
import {
  getMPRLayoutId,
  isMprProtocol,
  Layout,
  VOLUME_ORIENTATIONS,
} from './hanging-protocols/mpr';
import {
  PositionPresentation,
  Presentations,
} from '@ohif/extension-cornerstone/src/types/Presentation';
// OHIF 3.11: the position/LUT presentation state moved out of stateSyncService
// into zustand stores owned by the cornerstone extension. Reading them off
// stateSyncService now yields `undefined`, which broke the MIP camera/VOI
// save-restore (and made leaving MIP throw, so MIP could not be toggled off).
import {
  useLutPresentationStore,
  usePositionPresentationStore,
} from '@ohif/extension-cornerstone';

type VolumeOrientation = Internal.VolumeOrientation;

export function getCommandsModule({
  servicesManager,
  commandsManager,
  extensionManager,
}: {
  servicesManager: ServicesManager;
  commandsManager: CommandsManager;
  extensionManager: ExtensionManager;
}) {
  const {
    viewportGridService,
    uiModalService,
    displaySetService,
    hangingProtocolService,
    stateSyncService,
    ReduxStoreService,
    cornerstoneViewportService,
    uiNotificationService,
  } = servicesManager.services;

  const utilityModule = extensionManager.getModuleEntry(
    '@ohif/extension-cornerstone.utilityModule.common'
  );

  const { getEnabledElement: OHIFgetEnabledElement } = utilityModule.exports;

  function _getViewportElement(viewportId: string) {
    const { element } = OHIFgetEnabledElement(viewportId) || {};
    const enabledElement = getEnabledElement(element);
    return enabledElement;
  }

  function _getActiveViewportEnabledElement() {
    const { activeViewportId } = viewportGridService.getState();
    return _getViewportElement(activeViewportId);
  }

  function _getViewportOrientation(
    viewport: Types.IViewport
  ): VolumeOrientation {
    return viewport.options.orientation as VolumeOrientation;
  }

  function _getDisplaySetInstanceUID() {
    const { activeViewportId, viewports } = viewportGridService.getState();
    const viewport = viewports.get(activeViewportId) || {
      displaySetInstanceUIDs: [],
    };
    const {
      displaySetInstanceUIDs: [displaySetInstanceUID],
    } = viewport;

    return displaySetInstanceUID;
  }

  function _getDisplaySet() {
    const displaySetInstanceUID = _getDisplaySetInstanceUID();
    if (!displaySetInstanceUID) {
      return null;
    }

    return displaySetService.getDisplaySetByUID(displaySetInstanceUID);
  }

  function _storeCurrentPresentation() {
    const { activeViewportId } = viewportGridService.getState();
    const csViewport =
      cornerstoneViewportService.getCornerstoneViewport(activeViewportId);

    const currentIndex = csViewport.getCurrentImageIdIndex();

    const { setPositionPresentation } = usePositionPresentationStore.getState();

    // A `PositionPresentation` carries no `id` — the id is the STORE KEY, and
    // the presentation itself is `{ viewportType, viewReference,
    // viewPresentation, initialImageIndex }` (extensions/cornerstone/src/types/
    // Presentation.ts). So read the ids off the viewport rather than off the
    // presentation, and write the shape the service actually consumes:
    // `_setPositionPresentation` only ever applies `viewReference` /
    // `viewPresentation`, which is why the previous
    // `{ presentation: { ...getCamera() } }` blob was silently dropped and the
    // slice was lost on every protocol switch.
    const presentationIds = cornerstoneViewportService
      .getViewportInfo(activeViewportId)
      ?.getPresentationIds();

    stateSyncService.store({
      previousPresentationId: {
        positionPresentationId: presentationIds?.positionPresentationId,
        lutPresentationId: presentationIds?.lutPresentationId,
      },
    });

    if (presentationIds?.positionPresentationId) {
      setPositionPresentation(presentationIds.positionPresentationId, {
        viewportType: csViewport.type,
        viewReference: csViewport.getViewReference(),
        viewPresentation: csViewport.getViewPresentation({
          pan: true,
          zoom: true,
        }),
        initialImageIndex: currentIndex,
      } as PositionPresentation);
    }

    // Store previous presentation id to restore onMIPExit
    cornerstoneViewportService.storePresentation({
      viewportId: activeViewportId,
    });
  }

  function _updateMipHangingProtocolCamera() {
    const { activeViewportId } = viewportGridService.getState();
    const csViewport =
      cornerstoneViewportService.getCornerstoneViewport(activeViewportId);

    const displaySetInstanceUID = _getDisplaySetInstanceUID();

    const currentIndex = csViewport.getCurrentImageIdIndex();
    const { voiRange } = csViewport.getProperties();
    const { windowWidth, windowCenter } = utilities.windowLevel.toWindowLevel(
      voiRange.lower,
      voiRange.upper
    );

    const { setPositionPresentation, getPositionPresentationId } =
      usePositionPresentationStore.getState();

    const mipViewport = mipProtocolModule.protocol.stages[0].viewports[0];

    // Generate the id with OHIF's own generator instead of hand-rolling
    // `axial&<uid>&0`. ViewportGridProvider REGENERATES
    // `viewportOptions.presentationIds` when the protocol is applied, so a
    // hand-made id is overwritten and the stored presentation is never found.
    // Concretely: the MIP display set sets `blendMode`, which makes the
    // generator insert a `custom` segment (`axial&custom&<uid>&0`) that the
    // hardcoded id was missing — so the MIP viewport fell back to the middle
    // slice on every toggle.
    const positionPresentationId = getPositionPresentationId({
      viewportOptions: mipViewport.viewportOptions,
      displaySetInstanceUIDs: [displaySetInstanceUID],
      displaySetOptions: mipViewport.displaySets,
    });

    setPositionPresentation(positionPresentationId, {
      viewportType: csViewport.type,
      viewReference: csViewport.getViewReference(),
      viewPresentation: csViewport.getViewPresentation({
        pan: true,
        zoom: true,
      }),
      initialImageIndex: currentIndex,
    } as PositionPresentation);

    // store previous and presentation for positionPresentationId
    _storeCurrentPresentation();

    // Override existing MIP protocol to set the camera
    mipViewport.viewportOptions.presentationIds = {
      positionPresentationId,
    };

    if (stateSyncService.slabThickness > 0) {
      mipViewport.displaySets[0].options.slabThickness =
        stateSyncService.slabThickness;
    } else {
      stateSyncService.slabThickness = 10;
    }

    mipViewport.displaySets[0].options.voi = {
      windowWidth,
      windowCenter,
    };

    // addProtocol override existing protocol with the same id.
    hangingProtocolService.addProtocol(
      mipProtocolModule.protocol.id,
      mipProtocolModule.protocol
    );
  }

  const actions = {
    // These activate a tool as if its toolbar button were pressed. They used to
    // do `document.getElementById('<toolName>')?.click()`, which only worked when
    // a toolbar button rendered with that exact id existed — and OHIF 3.11's
    // ui-next toolbar renders neither those ids nor a bare clickable element
    // (`ToolButton` wraps the button in a `data-cy` span), so every one of these
    // silently did nothing. They're reachable via hotkeys, so drive the tool
    // directly through `setToolActiveToolbar` (the command a tool button itself
    // runs) — robust whether or not the tool is on the toolbar. All three tools
    // are registered in the label-mode tool groups (see initToolGroups).
    triggerZoomButton: () => {
      commandsManager.runCommand('setToolActiveToolbar', { toolName: 'Zoom' });
    },
    triggerPanButton: () => {
      commandsManager.runCommand('setToolActiveToolbar', { toolName: 'Pan' });
    },
    triggerRectangleToolButton: () => {
      commandsManager.runCommand('setToolActiveToolbar', {
        toolName: 'GleamerRectangleTool',
      });
    },
    characterisePatient: () => {
      if (document.getElementById('patient-characterisation-form')) {
        uiModalService.hide();
      } else {
        document.getElementById(BUTTON_IDS.PATIENT)?.click();
      }
    },
    characteriseInstance: () => {
      if (document.getElementById('instance-characterisation-form')) {
        uiModalService.hide();
      } else {
        document.getElementById(BUTTON_IDS.INSTANCE)?.click();
      }
    },
    characteriseReport: () => {
      if (document.getElementById('studyReport-characterisation-form')) {
        uiModalService.hide();
        return;
      }
      const displaySet = _getDisplaySet();

      if (!displaySet) {
        return;
      }

      const buttonId = `${BUTTON_IDS.REPORT}-${displaySet.StudyInstanceUID}`;
      document.getElementById(buttonId)?.click();
    },
    toggleViewportOverlay: () => {
      window.dispatchEvent(new Event(OVERLAY_EVENTS.OVERLAY_STATE_CHANGED));
    },
    deleteNearbyRegion: () => {
      eventTarget.dispatchEvent(
        new Event(OBSERVATION_EVENTS.DELETE_NEARBY_REGION)
      );
    },
    toggleRegionLabel: () => {
      const { global } = annotation.config.style.getDefaultToolStyles();
      annotation.config.style.setDefaultToolStyles({
        global: {
          ...global,
          textBoxFontSize: global.textBoxFontSize === '1' ? '0' : '1',
        },
      });
      const renderingEngine = cornerstoneViewportService.getRenderingEngine();
      renderingEngine.render();
    },
    toggleGleamerMPR: () => {
      const DEFAULT_MPR_HP_ID =
        '@gleamer/gdata-extension.hangingProtocolModule.mpr-axial-sagittal-coronal';

      const hpInfo = hangingProtocolService.getState();

      if (hpInfo.protocolId === mipProtocolModule.protocol.id) {
        uiNotificationService.show({
          title: 'Apply MPR',
          message:
            'MPR cannot be applied to MIP views. Exit MIP mode and use MPR with Crosshair tool instead.',
          type: 'error',
          duration: 3000,
        });
        return false;
      }

      const { mpr } = stateSyncService.getState().taskLayout || {
        mpr: DEFAULT_MPR_HP_ID,
      };

      if (isMprProtocol(hpInfo.protocolId)) {
        const { previousHangingProtocol } = stateSyncService.getState();
        commandsManager.run({
          commandName: 'setHangingProtocol',
          commandOptions: {
            protocolId: previousHangingProtocol?.protocolId || 'default',
          },
          context: 'DEFAULT',
        });
      } else {
        _storeCurrentPresentation();
        stateSyncService.getState().previousHangingProtocol = {
          protocolId: hpInfo.protocolId,
        };
        commandsManager.run({
          commandName: 'toggleHangingProtocol',
          commandOptions: {
            protocolId: mpr || DEFAULT_MPR_HP_ID,
          },
          context: 'DEFAULT',
        });
      }
    },
    toggleMIP: () => {
      const hpInfo = hangingProtocolService.getState();
      if (isMprProtocol(hpInfo.protocolId)) {
        uiNotificationService.show({
          title: 'Apply MIP',
          message:
            'MIP cannot be applied to MPR views. Use Crosshair tool instead.',
          type: 'error',
          duration: 3000,
        });
        return false;
      }

      if (hpInfo.protocolId !== mipProtocolModule.protocol.id) {
        _updateMipHangingProtocolCamera();
      }

      commandsManager.run({
        commandName: 'toggleHangingProtocol',
        commandOptions: {
          protocolId: mipProtocolModule.protocol.id,
        },
        context: 'DEFAULT',
      });
    },
    triggerMIPButton: () => {
      // Drive the real toolbar button so the click goes through
      // toolbarService.recordInteraction and the button's toggled state stays in
      // sync — calling toggleMIP directly would leave it out of step.
      //
      // OHIF 3.11: ui-next's ToolButton puts `data-cy` on a WRAPPER <span> (it
      // needs a non-disabled element for the tooltip trigger) with the <button>
      // inside, so querying `[data-cy="MIP"]` alone returns the span and
      // .click() silently does nothing. Accept either shape.
      const target = document.querySelector('[data-cy="MIP"]');
      const mipButton = (
        target instanceof HTMLButtonElement ? target : target?.querySelector('button')
      ) as HTMLButtonElement | null;
      mipButton?.click();
    },
    onExitMIP: () => {
      const { activeViewportId } = viewportGridService.getState();
      const displaySet = _getDisplaySet();

      const currentPresentation: Presentations =
        cornerstoneViewportService.getPresentations(activeViewportId);

      const { previousPresentationId } = stateSyncService.getState();
      const { positionPresentationStore } = usePositionPresentationStore.getState();
      const { lutPresentationStore } = useLutPresentationStore.getState();

      const ohifViewport =
        cornerstoneViewportService.getCornerstoneViewport(activeViewportId);

      const previousPositions: PositionPresentation = positionPresentationStore[
        previousPresentationId.positionPresentationId as string
      ] as PositionPresentation;

      const previousLut =
        lutPresentationStore[
          previousPresentationId.lutPresentationId as string
        ];

      // `displaySet` is checked here too, not just `previousPositions`:
      // `_getDisplaySet()` returns null whenever the active viewport entry has
      // no displaySetInstanceUIDs, which is exactly the state during a protocol
      // swap — i.e. when onProtocolExit fires. Without this, `displaySet.images`
      // throws inside the exit callback, aborting the restore half-applied and
      // leaving a stale initialImageIndex for the next exit to use.
      if (previousPositions && ohifViewport && displaySet) {
        const currentIndex = ohifViewport.getCurrentImageIdIndex();
        const imageIds = (displaySet.images || []).map(
          (image: { imageId?: string }) => image?.imageId
        );

        // Ask the MIP viewport WHICH IMAGE it is on rather than deriving it.
        // `numImages - 1 - currentIndex` assumes the volume's slice order is the
        // exact reverse of the stack's; that is a guess, and when it is wrong the
        // restored slice lands far from where the user was. An imageId match is
        // exact and ordering-agnostic.
        const mipImageId = (
          ohifViewport as { getCurrentImageId?: () => string | undefined }
        ).getCurrentImageId?.();
        const exactIndex = mipImageId ? imageIds.indexOf(mipImageId) : -1;

        if (exactIndex >= 0) {
          previousPositions.initialImageIndex = exactIndex;
        } else {
          // No imageId match. `getCurrentImageIdIndex()` is NOT a slice index on
          // a volume viewport — CS3D scales it by slab thickness, so it counts
          // slab steps. Inverting it against getNumImages() mixes index spaces:
          // exiting at slice 150 of 300 with a 10mm slab yields 300-1-15 = 284,
          // and a reformatted series can drive it negative. Clamp so a bad guess
          // can never be out of range, and leave the entry slice untouched when
          // the guess is not representable — an unchanged slice is recoverable,
          // a silently wrong one is not.
          const numImages = displaySet.getNumImages?.() ?? imageIds.length;
          const guessed = numImages - 1 - currentIndex;
          if (Number.isFinite(guessed) && guessed >= 0 && guessed < numImages) {
            previousPositions.initialImageIndex = guessed;
          }
        }

        // `_setStackViewport` resolves `viewReference.referencedImageId` AFTER
        // `initialImageIndex` and lets it win. Keep a reference only when the
        // stack can actually resolve it, otherwise it either pins the entry
        // slice (stale) or resolves to -1 and drops us on the default slice.
        const ref = (
          ohifViewport as { getViewReference?: () => { referencedImageId?: string } }
        ).getViewReference?.();
        if (ref?.referencedImageId && imageIds.includes(ref.referencedImageId)) {
          previousPositions.viewReference = ref as typeof previousPositions.viewReference;
        } else {
          delete previousPositions.viewReference;
        }
        if (previousLut?.presentation) {
          previousLut.presentation.voiRange =
            currentPresentation.lutPresentation?.presentation?.voiRange;
        }
      }
    },
    switchMPRViews: () => {
      const hpInfo = hangingProtocolService.getState();
      if (!isMprProtocol(hpInfo.protocolId)) {
        return;
      }

      const { activeViewportId, viewports } = viewportGridService.getState();

      const isMainViewportActive =
        viewports.get(activeViewportId).positionId === '0-0';
      if (isMainViewportActive) {
        // No viewport to switch
        return;
      }

      const currentPositionId = viewports.get(activeViewportId).positionId;
      const { viewport } = _getActiveViewportEnabledElement();
      const orientationToUse = _getViewportOrientation(viewport);

      const mainViewportId = Array.from(viewports.values()).find(
        viewport => viewport.positionId === '0-0'
      )?.viewportId;
      const { viewport: mainViewport } = _getViewportElement(mainViewportId);
      const currentOrientation = _getViewportOrientation(mainViewport);

      const unmodifiedOrientation = VOLUME_ORIENTATIONS.find(
        orientation =>
          orientation !== orientationToUse && orientation !== currentOrientation
      );

      const layoutToUse: Layout = {
        main: orientationToUse,
        top:
          currentPositionId === '1-0'
            ? currentOrientation
            : unmodifiedOrientation,
        bottom:
          currentPositionId === '2-0'
            ? currentOrientation
            : unmodifiedOrientation,
      };

      const protocolToUseId = getMPRLayoutId(layoutToUse);

      commandsManager.run({
        commandName: 'setHangingProtocol',
        commandOptions: {
          protocolId: protocolToUseId,
          reset: true,
        },
        context: 'DEFAULT',
      });
    },

    cancel: () => {
      ReduxStoreService.dispatch(ActionCreators.undo());
    },
    redo: () => {
      ReduxStoreService.dispatch(ActionCreators.redo());
    },
  };

  const definitions = {};
  Object.keys(actions).forEach(actionName => {
    definitions[actionName] = {
      commandFn: actions[actionName],
      storeContexts: [],
      options: {},
    };
  });

  return {
    actions,
    definitions,
    defaultContext: 'CORNERSTONE',
  };
}
